import { DOCUMENT } from '@angular/common';
import { Component, OnInit, Inject, DestroyRef, inject, signal, input, output } from '@angular/core';
import { Breakpoints } from '@angular/cdk/layout';
import { DateService } from '@app/services/date.service';
import moment from 'moment';
import { BehaviorSubject, from, Observable, of } from 'rxjs';
import { bufferCount, concatMap, delay, map, finalize } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MediaService } from '@app/services/media.service';

interface Month {
    id: string; // 2021-09-28
    value: string;
}

interface Calendar {
    w_0: Month[],
    w_1: Month[],
    w_2: Month[],
    w_3: Month[],
    w_4: Month[],
    w_5: Month[],
    header?: string;
    class?: string;
}

@Component({
    selector: 'clever-cleverdatepicker',
    templateUrl: './cleverdatepicker.component.html',
    styleUrls: ['./cleverdatepicker.component.scss'],
    standalone: false
})
export class CleverdatepickerComponent implements OnInit {
    mode = input<string>('');
    start = input<string>('');
    end = input<string>('');
    mindauer = input<number>(-1);
    maxdauer = input<number>(-1);
    isOne = input<boolean>(false);

    sendStart = output<string>();
    sendEnd = output<string>();
    sendRes = output<any>();


    header_week: string[] = ['MO', 'DI', 'MI', 'DO', 'FR', 'SA', 'SO'];

    currentFisrtDate = signal<string>(moment().startOf('month').format('YYYY-MM-DD'));
    currentSecondDate = signal<string>(moment().startOf('month').format('YYYY-MM-DD'));

    first_month = signal<Calendar>(null);
    second_month = signal<Calendar>(null);
    calendars: Observable<Calendar[]>;

    today = signal<string>(moment().format('YYYY-MM-DD'));
    tmp_end = signal<Month>(null);
    first_header = signal<string>('');
    second_header = signal<string>('');
    selecteddif = signal<number>(null);

    showcalMobile = signal<boolean>(false);
    dauererrormin = signal<boolean>(false);
    dauererrormax = signal<boolean>(false);

    initDate: BehaviorSubject<{ first: string, second: string }>;
    temInit: BehaviorSubject<Month>;

    destroyRef = inject(DestroyRef);

    section1 = [
        {
            name: 'Genaue Reisedaten',
            minDuration: -1,
            maxDuration: -1,
        },
        {
            name: '1 Woche',
            minDuration: 7,
            maxDuration: 7,
        },
        {
            name: '2 Wochen',
            minDuration: 14,
            maxDuration: 14,
        },
        {
            name: '3 Wochen',
            minDuration: 21,
            maxDuration: 21,
        },
        {
            name: '4 Wochen',
            minDuration: 27,
            maxDuration: 27,
        },
        {
            name: '2-3 Tage',
            minDuration: 2,
            maxDuration: 3,
        },
        {
            name: '4-7 Tage',
            minDuration: 4,
            maxDuration: 7,
        },
        {
            name: '5-8 Tage',
            minDuration: 5,
            maxDuration: 8,
        },
        {
            name: '8-11 Tage',
            minDuration: 8,
            maxDuration: 11,
        },
        {
            name: '12-15 Tage',
            minDuration: 12,
            maxDuration: 15,
        },
        {
            name: '16-21 Tage',
            minDuration: 16,
            maxDuration: 21,
        },
        // {
        //     name: '22-30 Tage',
        //     minDuration: 22,
        //     maxDuration: 30,
        // },
        // {
        //     name: '31-56 Tage',
        //     minDuration: '31',
        //     maxDuration: '56',
        // },
    ];

    local_start = signal<string>('');
    local_end = signal<string>('');
    local_dauertitle = signal<string>('');
    local_mindauer = signal<number>(-1);
    local_maxdauer = signal<number>(-1);
    setcustomdauer = signal<boolean>(false);

    custom_mindauer: number;
    custom_maxdauer: number;
    custom_dauertitle = signal<string>('');
    showModal = signal<boolean>(false);

    constructor(
        protected mediaService: MediaService,
        protected dateService: DateService,
        @Inject(DOCUMENT) protected document: any,
    ) {
        this.initDate = new BehaviorSubject(null);
        this.temInit = new BehaviorSubject(null);

    }

    ngOnInit(): void {
        this.local_end.set(this.end());
        this.local_start.set(this.start());
        this.local_mindauer.set(this.mindauer());
        this.local_maxdauer.set(this.maxdauer());
        this.local_dauertitle.set(this.getDauertitle());
        this.currentFisrtDate.set(moment(this.local_start()).startOf('month').format('YYYY-MM-DD'));
        if (this.local_start() && this.local_end()) {
            this.selecteddif.set(moment(this.local_end()).diff(moment(this.local_start()), 'day'));
        }
        this.initSub();
        this.initCals();
    }

    closeModal(): void {
        this.showModal.set(false);
    }

    openModal(): void {
        this.showModal.set(true);
    }

    getDauertitle(): string {
        let title = '';
        for (let i = 0; i < this.section1.length; i++) {
            if (this.local_mindauer() === this.section1[i].minDuration &&
                this.local_maxdauer() === this.section1[i].maxDuration) {
                title = this.section1[i].name;
                i = this.section1.length;
            }
        }

        if (title.length === 0) {
            if (this.local_mindauer() === this.local_maxdauer()) {
                title = this.local_mindauer() + (this.local_mindauer() === 1 ? ' Tag' : ' Tage');
            } else {
                title = this.local_mindauer() + '-' + this.local_maxdauer() + ' Tage';
            }

            this.custom_dauertitle.set(title);
        } else {
            this.custom_dauertitle.set('');
        }


        return title;
    }

    async checkminDauer(dauer) {
        this.dauererrormin.set(Number(dauer) > this.selecteddif());
    }

    async checkmaxDauer(dauer) {
        this.dauererrormax.set(Number(dauer) > this.selecteddif());
    }

    async setCustomDauer(min, max) {
        this.dauererrormax.set(Number(max) > this.selecteddif());
        this.dauererrormin.set(Number(min) > this.selecteddif());
        if (this.dauererrormax() || this.dauererrormax()) {
            return;
        } else {
            this.local_mindauer.set(Number(min));
            this.local_maxdauer.set(Number(max));
            this.local_dauertitle.set(this.getDauertitle());

            this.setcustomdauer.set(false);
        }

        this.closeModal();
    }

    initSub() {
        this.initDate.asObservable().pipe(
            takeUntilDestroyed(this.destroyRef)
        ).subscribe((data: any) => {
            if (data) {
                this.initCal();
            }
        });
        this.initDate.next({ first: this.currentFisrtDate(), second: this.currentSecondDate() });

        this.temInit.asObservable().pipe(
            takeUntilDestroyed(this.destroyRef)
        ).subscribe((tmpdate) => {
            if (tmpdate?.id?.length > 0) {
                if (!this.local_end() || this.local_end()?.length === 0) {
                    this.tmp_end.set(tmpdate);
                } else {
                    this.tmp_end.set(null);
                }
            } else {
                this.tmp_end.set(null);
            }
        });
    }

    initCal() {
        this.first_month.set(this.dateService.initMonth(this.currentFisrtDate()));
        this.currentSecondDate.set(moment(this.currentFisrtDate()).startOf('month').add(1, 'months').format('YYYY-MM-DD'));
        this.first_header.set(this.currentFisrtDate());
        this.second_month.set(this.dateService.initMonth(this.currentSecondDate()));
        this.second_header.set(this.currentSecondDate());
    }

    initCals() {
        const count = 3;
        const items = [];
        this.dateService.getBigCalendar().pipe(
            takeUntilDestroyed(this.destroyRef)
        ).subscribe((arrr) => {
            const source$ = from(arrr);
            this.calendars = source$.pipe(
                bufferCount(count),
                concatMap((items, index) => of(items).pipe(delay(index === 0 ? 0 : 1))),
                map((arr: any) => {
                    items.push(...arr);
                    return [...items];
                }),
                finalize(() => {
                    setTimeout(() => {
                        this.gotopos();
                    }, 100);
                })
            );
        });
    }

    async gotopos() {
        const cl = 'cal_' + moment(this.local_start()).format('YYYY-MM');
        const invalidElements = this.document.getElementById(cl);
        if (invalidElements) {
            invalidElements.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }

    selectDay(md: Month) {
        if (md?.id?.length > 0) {
            if (this.isOne()) {
                this.local_end.set('');
                this.local_start.set(md.id);
            } else if (this.local_end()?.length > 0 && this.local_start()?.length > 0) {
                this.local_end.set('');
                this.local_start.set(md.id);
            } else {
                if (this.local_start()?.length > 0 && this.local_start() < md.id) {
                    this.local_end.set(md.id);
                } else {
                    this.local_start.set(md.id);
                }
            }
        }
        if (this.local_start() && this.local_end()) {
            this.selecteddif.set(moment(this.local_end()).diff(moment(this.local_start()), 'day'));
        }

        if (this.local_mindauer() > this.selecteddif() || this.local_maxdauer() > this.selecteddif()) {
            this.local_mindauer.set(-1);
            this.local_maxdauer.set(-1);
            this.local_dauertitle.set('Genaue Reisedaten');


            this.dauererrormax.set(false);
            this.dauererrormin.set(false);
            this.custom_dauertitle.set('');
            this.custom_mindauer = null;
            this.custom_maxdauer = null;
        } else {
            this.dauererrormax.set(Number(this.custom_maxdauer) > this.selecteddif());
            this.dauererrormin.set(Number(this.custom_mindauer) > this.selecteddif());
        }



        // this.sendStart.emit(this.start);
        // this.sendEnd.emit(this.end);
        // this.sendRes.emit({ start: this.start, end: this.end });
    }

    async sendDatas() {
        this.sendRes.emit({ start: this.local_start(), end: this.local_end(), minDuration: this.local_mindauer(), maxDuration: this.local_maxdauer(), dauer_name: this.local_dauertitle() });
    }

    async selectDauer(item) {
        this.local_mindauer.set(item.minDuration);
        this.local_maxdauer.set(item.maxDuration);
        this.local_dauertitle.set(item.name);

        this.dauererrormax.set(false);
        this.dauererrormin.set(false);
        this.custom_dauertitle.set('');
        this.custom_mindauer = null;
        this.custom_maxdauer = null;

    }

    selectTmp_Day(md) {
        this.temInit.next(md);
    }

    previus() {
        this.currentFisrtDate.set(moment(this.currentFisrtDate()).startOf('month').subtract(1, 'months').format('YYYY-MM-DD'));
        this.initDate.next({ first: this.currentFisrtDate(), second: this.currentSecondDate() });
    }

    next() {
        this.currentFisrtDate.set(moment(this.currentFisrtDate()).startOf('month').add(1, 'months').format('YYYY-MM-DD'));
        this.initDate.next({ first: this.currentFisrtDate(), second: this.currentSecondDate() });
    }
}
